import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React from 'react';
import { createRoot } from 'react-dom/client';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';
import { Protocolo } from "../../logic/domain/entities/protocolo";
import { PrestacionHistorico } from "../../logic/domain/entities/prestacionHistorico";

interface LoadingProgressCallback {
  (current: number, total: number, message: string): void;
}

/**
 * Convierte HTML a PDF con soporte para:
 * - Headers y footers repetidos en cada página
 * - Gráficos históricos de prestaciones
 * - CSS con scope para evitar conflictos con la UI principal
 * - Indicador de progreso durante la generación
 */
export const saveHtmlToPdf = async (
  response: string, 
  protocolo: Protocolo, 
  historicos?: PrestacionHistorico[],
  onProgress?: LoadingProgressCallback
) => {
  const getNiceStep = (rawStep: number) => {
    if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
    const power = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const fraction = rawStep / power;

    let niceFraction: number;
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 2.5) niceFraction = 2.5;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;

    return niceFraction * power;
  };

  const buildNiceDomainAndTicks = (
    min: number,
    max: number,
    targetTickCount = 6
  ): { domain: [number, number]; ticks: number[] } | null => {
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    if (targetTickCount < 2) targetTickCount = 2;
    if (max < min) [min, max] = [max, min];

    if (min === max) {
      const pad = Math.max(Math.abs(min) * 0.05, 1);
      min -= pad;
      max += pad;
    }

    let step = getNiceStep((max - min) / (targetTickCount - 1));
    let niceMin = Math.floor(min / step) * step;
    let niceMax = Math.ceil(max / step) * step;

    const countTicks = () => Math.round((niceMax - niceMin) / step) + 1;

    // Si quedaron demasiados ticks para el alto del gráfico, aumentamos el step.
    let guard = 0;
    while (countTicks() > targetTickCount + 2 && guard < 10) {
      step = getNiceStep(step * 2);
      niceMin = Math.floor(min / step) * step;
      niceMax = Math.ceil(max / step) * step;
      guard++;
    }

    const ticks: number[] = [];
    for (let v = niceMin; v <= niceMax + step / 2; v += step) {
      // Evitar errores flotantes tipo 3999.999999
      ticks.push(Number(v.toFixed(10)));
    }

    return { domain: [niceMin, niceMax], ticks };
  };

  const escapeHtml = (value: unknown) =>
    String(value ?? '').replace(/[&<>"']/g, (ch) => {
      switch (ch) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        case "'":
          return '&#39;';
        default:
          return ch;
      }
    });

  const formatAxisNumber = (value: unknown) => {
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) return String(value ?? '');
    return new Intl.NumberFormat('es-CL', {
      maximumFractionDigits: 2,
    }).format(num);
  };

  // IMPORTANTE:
  // - El HTML de respuesta suele traer <style> globales (body/table/etc). Si los inyectamos tal cual,
  //   pueden afectar la UI principal y provocar un "flash".
  // - Intentar renderizar dentro de un iframe puede dar PDFs en blanco con html2canvas.
  // Solución: renderizamos en el mismo document pero "scopeamos" el CSS al contenedor temporal.
  
  const totalSteps = 3 + (historicos?.length || 0);
  let currentStep = 0;
  onProgress?.(currentStep, totalSteps, 'Preparando documento...');

  const parser = new DOMParser();
  const parsedDoc = parser.parseFromString(response, 'text/html');
  const extractedStyles = Array.from(parsedDoc.querySelectorAll('style'))
    .map((s) => s.textContent ?? '')
    .join('\n');

  // Tomamos el body del HTML parseado para evitar meter <html>/<head> dentro del contenedor.
  const extractedBody = parsedDoc.body?.innerHTML?.trim();
  const htmlBody = extractedBody && extractedBody.length > 0 ? extractedBody : response;

  const renderWidthPx = 1000;
  const sandboxId = `pdf-sandbox-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const scopeCss = (cssText: string, scopeSelector: string) => {
    // Sanitizar posiciones fijas para evitar que algo del informe aparezca en la UI
    // aunque el contenedor esté off-screen.
    const sanitized = cssText
      .replace(/position\s*:\s*fixed/gi, 'position: absolute')
      .replace(/position\s*:\s*sticky/gi, 'position: static')
      // html2canvas renderiza con media=screen; si el informe trae reglas @media print,
      // las volvemos aplicables para que mantenga el layout esperado.
      .replace(/@media\s+print\b/gi, '@media all');

    // Scoping simple (suficiente para CSS del informe). No procesa @media complejos.
    return sanitized.replace(/(^|})(\s*[^@}{][^{]+)\{/g, (_match, sep, selectorList) => {
      const scopedSelectors = String(selectorList)
        .split(',')
        .map((raw) => {
          const selector = raw.trim();
          if (!selector) return selector;

          // Evitar doble scoping
          if (selector.includes(scopeSelector)) return selector;

          // Si el selector empieza por html/body, lo reemplazamos por el scope
          const withoutHtmlBody = selector.replace(/^(html|body)\b/i, '').trim();
          return withoutHtmlBody ? `${scopeSelector} ${withoutHtmlBody}` : `${scopeSelector}`;
        })
        .join(', ');

      return `${sep}${scopedSelectors}{`;
    });
  };

  const scopedStyles = scopeCss(extractedStyles, `#${sandboxId}`);

  const tempElement = document.createElement('div');
  tempElement.id = sandboxId;
  tempElement.style.width = `${renderWidthPx}px`;
  tempElement.style.minHeight = 'auto';
  tempElement.style.position = 'fixed';
  tempElement.style.top = '0';
  tempElement.style.left = '-10000px';
  tempElement.style.backgroundColor = 'white';
  tempElement.style.pointerEvents = 'none';
  tempElement.style.zIndex = '-1';
  tempElement.innerHTML = `
    <style>
      ${scopedStyles}
      /* Defaults seguros para el contenedor */
      #${sandboxId} { background: white; }
      #${sandboxId} #pdf-page { width: ${renderWidthPx}px; margin: 0; padding: 0; }
      #${sandboxId} #pdf-page * { box-sizing: border-box; }
      #${sandboxId} #pdf-page img { max-width: 100%; height: auto; }
      #${sandboxId} #pdf-page table { width: 100% !important; max-width: 100%; margin: 0 !important; }
      #${sandboxId} #pdf-page > table { width: 100% !important; }
    </style>
    <div id="pdf-page" style="width: ${renderWidthPx}px; padding: 0px; margin: 0; box-sizing: border-box; background: white;">
      ${htmlBody}
    </div>
  `;
  document.body.appendChild(tempElement);

  const waitForSandboxResources = async () => {
    const images = Array.from(tempElement.querySelectorAll('img')) as HTMLImageElement[];
    await Promise.all(
      images.map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise<void>((resolve) => {
                img.onload = () => resolve();
                img.onerror = () => resolve();
              })
      )
    );

    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {
        // Ignorar
      }
    }

    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  };

  try {
    await waitForSandboxResources();

    const pdfPage = tempElement.querySelector('#pdf-page') as HTMLElement | null;
    if (!pdfPage) {
      throw new Error('No se encontró el contenedor del informe para generar el PDF');
    }

    // Buscar la tabla principal y su thead
    const mainTable = pdfPage.querySelector('table');
    const theadElement = mainTable?.querySelector('thead');
    const tbodyElement = mainTable?.querySelector('tbody');
    const tfootElement = mainTable?.querySelector('tfoot');
    let headerCanvas = null;
    let headerHeight = 0;
    let footerCanvas = null;
    let footerHeight = 0;

    const normalizeCellText = (value: string) =>
      value
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    const theadHeaderTexts = (() => {
      if (!theadElement) return [] as string[];
      const lastHeaderRow = theadElement.querySelector('tr:last-child');
      if (!lastHeaderRow) return [] as string[];
      return Array.from(lastHeaderRow.querySelectorAll('th,td'))
        .map((cell) => normalizeCellText(cell.textContent ?? ''))
        .filter(Boolean);
    })();

    // Si hay thead, renderizarlo con la estructura completa
    if (theadElement && mainTable) {
      onProgress?.(++currentStep, totalSteps, 'Renderizando encabezado...');
      
      // Clonar la tabla original con sus atributos
      const tableClone = mainTable.cloneNode(false) as HTMLElement;
      Array.from(mainTable.attributes).forEach(attr => {
        tableClone.setAttribute(attr.name, attr.value);
      });
      const theadClone = theadElement.cloneNode(true) as HTMLElement;
      tableClone.appendChild(theadClone);

      const headerDiv = document.createElement('div');
      headerDiv.style.position = 'absolute';
      headerDiv.style.left = '0';
      headerDiv.style.top = '0';
      headerDiv.style.width = `${renderWidthPx}px`;
      headerDiv.style.padding = '0px';
      headerDiv.style.boxSizing = 'border-box';
      headerDiv.style.backgroundColor = 'white';
      headerDiv.appendChild(tableClone);
      // Importante: el header debe vivir dentro del sandbox para que el CSS scopeado le aplique.
      tempElement.appendChild(headerDiv);

      headerCanvas = await html2canvas(headerDiv, { scale: 1.5, logging: false, useCORS: true });
      headerHeight = headerCanvas.height;
      tempElement.removeChild(headerDiv);
    }

    // Si hay tfoot, renderizarlo con la estructura completa (para usar como pie repetido)
    if (tfootElement && mainTable) {
      const tableClone = mainTable.cloneNode(false) as HTMLElement;
      Array.from(mainTable.attributes).forEach((attr) => {
        tableClone.setAttribute(attr.name, attr.value);
      });
      const tfootClone = tfootElement.cloneNode(true) as HTMLElement;
      tableClone.appendChild(tfootClone);

      const footerDiv = document.createElement('div');
      footerDiv.style.position = 'absolute';
      footerDiv.style.left = '0';
      footerDiv.style.bottom = '0';
      footerDiv.style.width = `${renderWidthPx}px`;
      footerDiv.style.padding = '0px';
      footerDiv.style.boxSizing = 'border-box';
      footerDiv.style.backgroundColor = 'white';
      footerDiv.appendChild(tableClone);
      // Importante: el footer debe vivir dentro del sandbox para que el CSS scopeado le aplique.
      tempElement.appendChild(footerDiv);

      footerCanvas = await html2canvas(footerDiv, { scale: 1.5, logging: false, useCORS: true });

      // Recortar el canvas al contenido real del tfoot para evitar alturas grandes
      // (algunos CSS pueden hacer que la tabla clone tenga alto excesivo).
      try {
        const footerDivRect = footerDiv.getBoundingClientRect();
        const footerContentEl = (footerDiv.querySelector('tfoot') || footerDiv.querySelector('table') || footerDiv) as HTMLElement;
        const footerContentRect = footerContentEl.getBoundingClientRect();
        const relTopCss = Math.max(0, footerContentRect.top - footerDivRect.top);
        const relHeightCss = Math.max(1, footerContentRect.height);

        const scaleY = footerDivRect.height > 0 ? (footerCanvas.height / footerDivRect.height) : 1;
        const cropY = Math.max(0, Math.floor(relTopCss * scaleY));
        const cropH = Math.min(footerCanvas.height - cropY, Math.ceil(relHeightCss * scaleY));

        if (cropH > 0 && cropH < footerCanvas.height) {
          const cropped = document.createElement('canvas');
          cropped.width = footerCanvas.width;
          cropped.height = cropH;
          const croppedCtx = cropped.getContext('2d');
          croppedCtx?.drawImage(
            footerCanvas,
            0,
            cropY,
            footerCanvas.width,
            cropH,
            0,
            0,
            footerCanvas.width,
            cropH
          );
          footerCanvas = cropped;
        }
      } catch {
        // Ignorar
      }

      footerHeight = footerCanvas.height;
      tempElement.removeChild(footerDiv);
    }

    // IMPORTANTE: si el HTML del informe trae el encabezado dentro del mismo <table>
    // (y a veces repetido también como primeras filas del <tbody>), al pegar headerCanvas
    // debemos evitar que el canvas del contenido lo vuelva a incluir.
    if (mainTable && theadElement) {
      try {
        theadElement.remove();
      } catch {
        // Ignorar
      }
    }

    if (mainTable && tfootElement) {
      try {
        tfootElement.remove();
      } catch {
        // Ignorar
      }
    }

    const isDuplicateHeaderRow = (row: HTMLTableRowElement) => {
      // Si usa <th> dentro del tbody, casi seguro es un header duplicado
      if (row.querySelector('th')) return true;

      const rowTexts = Array.from(row.querySelectorAll('td,th'))
        .map((cell) => normalizeCellText(cell.textContent ?? ''))
        .filter(Boolean);

      if (rowTexts.length === 0) return false;

      if (theadHeaderTexts.length > 0 && rowTexts.length === theadHeaderTexts.length) {
        const same = rowTexts.every((t, idx) => t === theadHeaderTexts[idx]);
        if (same) return true;
      }

      // Heurística extra (caso encabezado no esté exactamente igual al thead)
      const joined = rowTexts.join(' | ');
      if (joined.includes('analisis') && joined.includes('resultado')) return true;
      if (joined.includes('intervalo') && joined.includes('referencia')) return true;
      return false;
    };

    if (tbodyElement) {
      // Remover todas las filas iniciales del tbody que parezcan encabezados duplicados
      // (algunos informes repiten 1 o más filas de header al comenzar el cuerpo).
      // Limitamos a un pequeño número para evitar borrar contenido legítimo.
      let guard = 0;
      while (guard < 5) {
        const firstRow = tbodyElement.querySelector(':scope > tr') as HTMLTableRowElement | null;
        if (!firstRow) break;
        if (!isDuplicateHeaderRow(firstRow)) break;
        firstRow.remove();
        guard++;
      }
    }

    // Renderizar el contenido completo
    onProgress?.(++currentStep, totalSteps, 'Renderizando contenido principal...');
    const contentCanvas = await html2canvas(pdfPage, { scale: 1.5, logging: false, useCORS: true });

    // Crear el PDF
    onProgress?.(++currentStep, totalSteps, 'Generando páginas del PDF...');
    const pdf = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = 230;
    const pageHeight = 297;
    const marginTop = 15;
    const marginBottom = 15;
    const marginSide = 10;
    const imgWidth = pageWidth - (marginSide * 2);

    // Gaps para evitar que el contenido "toque" el header/footer por redondeos
    const headerGapMm = 2;
    const footerGapMm = 2;
    
    // Calcular altura del header en mm
    const headerHeightMm = headerCanvas ? (headerHeight * imgWidth) / contentCanvas.width : 0;
    const footerHeightMm = footerCanvas ? (footerHeight * imgWidth) / contentCanvas.width : 0;
    const usablePageHeight = pageHeight - marginTop - marginBottom;
    const contentAreaHeight = usablePageHeight - headerHeightMm - footerHeightMm - headerGapMm - footerGapMm;

    let yPosition = marginTop + headerHeightMm + headerGapMm; // Posición inicial después del header
    const footerY = pageHeight - marginBottom - footerHeightMm;

    // Calcular dónde empieza el contenido real bajo el encabezado (ej. el inicio del tbody)
    // usando medidas del DOM para evitar recortes por diferencias entre canvases.
    const pageRect = pdfPage.getBoundingClientRect();
    const pxPerCssPx = pageRect.width > 0 ? (contentCanvas.width / pageRect.width) : 2;

    let contentStartCssPx = 0;
    if (tbodyElement) {
      const tbodyRect = tbodyElement.getBoundingClientRect();
      contentStartCssPx = Math.max(0, tbodyRect.top - pageRect.top);
    } else if (mainTable) {
      const tableRect = mainTable.getBoundingClientRect();
      contentStartCssPx = Math.max(0, tableRect.top - pageRect.top);
    }

    // Ajuste pequeño para no cortar la primera línea si hay bordes/sombras
    const safetyCssPx = 2;
    let sourceY = Math.max(0, Math.round((contentStartCssPx - safetyCssPx) * pxPerCssPx));
    //let pageNumber = 0;

    // Primera pasada: renderizar todas las páginas sin numeración
    const pages: Array<{
      headerImgData?: string;
      footerImgData?: string;
      pageImgData?: string;
      heightToCopy: number;
    }> = [];

    const headerImgData = headerCanvas ? headerCanvas.toDataURL('image/png') : undefined;
    const footerImgData = footerCanvas ? footerCanvas.toDataURL('image/png') : undefined;

    while (sourceY < contentCanvas.height) {
      // Calcular cuánto contenido cabe en esta página (en px) para evitar redondeos
      const remainingHeightPx = contentCanvas.height - sourceY;
      const maxSourceHeightPx = Math.max(1, Math.floor((contentAreaHeight * contentCanvas.width) / imgWidth));
      const sourceHeight = Math.min(remainingHeightPx, maxSourceHeightPx);
      const heightToCopy = (sourceHeight * imgWidth) / contentCanvas.width;

      // Crear un canvas temporal con solo la porción de contenido para esta página
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = contentCanvas.width;
      pageCanvas.height = sourceHeight;
      
      const ctx = pageCanvas.getContext('2d');
      let pageImgData: string | undefined;
      if (ctx) {
        ctx.drawImage(
          contentCanvas,
          0, sourceY, // Posición de origen
          contentCanvas.width, sourceHeight, // Tamaño de origen
          0, 0, // Posición de destino
          contentCanvas.width, sourceHeight // Tamaño de destino
        );
        
        pageImgData = pageCanvas.toDataURL('image/png');
      }

      pages.push({
        headerImgData,
        footerImgData,
        pageImgData,
        heightToCopy
      });

      sourceY += sourceHeight;
      //pageNumber++;
    }

    // Segunda pasada: agregar páginas al PDF con numeración
    //const totalPages = pages.length;
    pages.forEach((pageData, index) => {
      if (index > 0) {
        pdf.addPage();
      }

      // Agregar encabezado
      if (pageData.headerImgData) {
        pdf.addImage(pageData.headerImgData, 'PNG', marginSide, marginTop, imgWidth, headerHeightMm);
      }

      // Agregar pie
      if (pageData.footerImgData) {
        pdf.addImage(pageData.footerImgData, 'PNG', marginSide, footerY, imgWidth, footerHeightMm);
      }

      // Agregar contenido
      if (pageData.pageImgData) {
        pdf.addImage(pageData.pageImgData, 'PNG', marginSide, yPosition, imgWidth, pageData.heightToCopy);
      }
    });

    // Guardar el número de páginas del informe principal
    //const mainReportPages = totalPages;

    // Agregar gráficos históricos si existen
    if (historicos && historicos.length > 0) {
      pdf.addPage();
      
      // Título de la sección
      const a4Width = 210; // Ancho real A4 en mm
      pdf.setFontSize(16);
      pdf.setTextColor(0, 163, 168);
      pdf.text('Antecedentes', a4Width / 2, 20, { align: 'center' });
      
      let chartYPosition = 30;
      
      for (let i = 0; i < historicos.length; i++) {
        const prestacion = historicos[i];
        onProgress?.(3 + i + 1, totalSteps, `Generando gráfico ${i + 1} de ${historicos.length}...`);
        const historico = prestacion.getHistorico();
        if (!historico || historico.length === 0) continue;
        
        const data = historico.map((item) => ({
          fecha: item.fecha,
          valor: parseFloat(String(item.valor).replace(',', '.')) || 0,
          muestra: item.muestra,
          unidad: item.unidad
        })).reverse();

        // Calcular si hay espacio en esta página
        const chartHeight = 70; // Altura aproximada del gráfico en mm
        if (chartYPosition + chartHeight > pageHeight - 20) {
          pdf.addPage();
          chartYPosition = 20;
        }

        // Crear contenedor para el gráfico
        const chartContainer = document.createElement('div');
        chartContainer.style.position = 'fixed';
        chartContainer.style.top = '-10000px';
        chartContainer.style.width = `${renderWidthPx}px`;
        chartContainer.style.height = '280px';
        chartContainer.style.backgroundColor = 'white';
        chartContainer.style.padding = '15px';
        chartContainer.style.fontFamily = 'Arial, sans-serif';
        chartContainer.style.overflow = 'hidden';
        chartContainer.style.marginRight = '20px';
        document.body.appendChild(chartContainer);

        // Obtener nombre de la prestación y valor de referencia (val_ref)
        const prestacionData = prestacion.getPrestacion();
        const valRefRaw =
          (typeof (prestacion as any).getValRef === 'function' ? (prestacion as any).getValRef() : '') ||
          historico.map((h: any) => h?.val_ref).find((v: any) => String(v ?? '').trim().length > 0) ||
          '';

        const valRefMinRaw =
          typeof (prestacion as any).getValRefMin === 'function' ? (prestacion as any).getValRefMin() : null;
        const valRefMaxRaw =
          typeof (prestacion as any).getValRefMax === 'function' ? (prestacion as any).getValRefMax() : null;

        let yDomain: [number, number] | undefined;
        if (Number.isFinite(valRefMinRaw) && Number.isFinite(valRefMaxRaw)) {
          let min = Number(valRefMinRaw);
          let max = Number(valRefMaxRaw);

          if (max < min) {
            const tmp = min;
            min = max;
            max = tmp;
          }

          const dataValues = data.map((d) => d.valor).filter((v) => Number.isFinite(v));
          const dataMin = dataValues.length > 0 ? Math.min(...dataValues) : min;
          const dataMax = dataValues.length > 0 ? Math.max(...dataValues) : max;

          // Margen: 5% del rango de referencia (si es 0, fallback razonable)
          const refRange = Math.max(0, max - min);
          const pad = refRange > 0 ? refRange * 0.05 : Math.max(Math.abs(max || min) * 0.05, 1);

          let domainMin = Math.min(min, dataMin) - pad;
          let domainMax = Math.max(max, dataMax) + pad;

          if (domainMin === domainMax) {
            const pad2 = Math.max(Math.abs(domainMin) * 0.05, 1);
            domainMin -= pad2;
            domainMax += pad2;
          }

          yDomain = [domainMin, domainMax];
        }

        const prestacionTitleHtml = escapeHtml(prestacionData);
        const valRefHtml = escapeHtml(valRefRaw);
        const valRefLabelHtml = valRefHtml ? `Intervalo referencia: ${valRefHtml}` : '';
        const valRefLineHtml = valRefLabelHtml
          ? `<div style="font-weight: normal; font-size: 10px; color: #333; margin-top: 2px;">${valRefLabelHtml}</div>`
          : '';

        // HTML del gráfico con tabla (proporción 2:1)
        chartContainer.innerHTML = `
          <div style="display: flex; gap: 10px; height: 100%; align-items: stretch;">
            <!-- Gráfico -->
            <div style="flex: 2; min-width: 0; padding: 10px; background: white; box-sizing: border-box;">
              <div style="margin-bottom: 6px;">
                <div style="font-weight: bold; font-size: 12px;">Antecedentes ${prestacionTitleHtml}</div>
                ${valRefLineHtml}
              </div>
              <div id="recharts-container-${i}" style="width: 100%; height: 210px;"></div>
            </div>
            <!-- Tabla -->
            <div style="flex: 1; min-width: 0; padding: 5px; background: white; overflow: hidden; box-sizing: border-box;">
              <table style="width: 100%; border-collapse: collapse; font-size: 9px;">
                <thead>
                  <tr>
                    <th style="border: 1px solid #ccc; padding: 3px; background: #00A3A8; color: white; text-align: center;">Nro.</th>
                    <th style="border: 1px solid #ccc; padding: 3px; background: #00A3A8; color: white; text-align: center;">Fecha</th>
                    <th style="border: 1px solid #ccc; padding: 3px; background: #00A3A8; color: white; text-align: center;">Muestra</th>
                    <th style="border: 1px solid #ccc; padding: 3px; background: #00A3A8; color: white; text-align: center;">Resultado</th>
                    <th style="border: 1px solid #ccc; padding: 3px; background: #00A3A8; color: white; text-align: center;">Unidad</th>
                  </tr>
                </thead>
                <tbody>
                  ${data.slice().reverse().map((item, idx) => `
                    <tr>
                      <td style="border: 1px solid #ccc; padding: 3px; text-align: center;">${data.length - idx}</td>
                      <td style="border: 1px solid #ccc; padding: 3px; text-align: center;">${item.fecha}</td>
                      <td style="border: 1px solid #ccc; padding: 3px; text-align: center;">${item.muestra}</td>
                      <td style="border: 1px solid #ccc; padding: 3px; text-align: center;">${item.valor}</td>
                      <td style="border: 1px solid #ccc; padding: 3px; text-align: center;">${item.unidad}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>
        `;
        // Renderizar Recharts para que la escala coincida con prestacionModal
        const rechartsContainer = chartContainer.querySelector(`#recharts-container-${i}`) as HTMLElement | null;
        let root: ReturnType<typeof createRoot> | null = null;
        if (rechartsContainer && data.length > 0) {
          root = createRoot(rechartsContainer);

          const LineChartAny = LineChart as any;
          const LineAny = Line as any;
          const CartesianGridAny = CartesianGrid as any;
          const XAxisAny = XAxis as any;
          const YAxisAny = YAxis as any;
          const TooltipAny = Tooltip as any;
          const ReferenceLineAny = ReferenceLine as any;

          // Usar el tamaño real calculado por layout (evita que el chart se "coma" el espacio de la tabla)
          const rect = rechartsContainer.getBoundingClientRect();
          const chartWidthPx = Math.max(250, Math.floor(rect.width || 0));
          const chartHeightPx = Math.max(160, Math.floor(rect.height || 0));

          const yAxisProps: any = { width: 70, tickFormatter: formatAxisNumber };
          if (yDomain) {
            const nice = buildNiceDomainAndTicks(yDomain[0], yDomain[1], 6);
            if (nice) {
              yAxisProps.domain = nice.domain;
              yAxisProps.ticks = nice.ticks;
            } else {
              yAxisProps.domain = yDomain;
              yAxisProps.tickCount = 6;
            }
          }

          const chartChildren: any[] = [];

          // Líneas de referencia (mínimo / máximo)
          if (Number.isFinite(valRefMinRaw)) {
            chartChildren.push(
              React.createElement(ReferenceLineAny, {
                y: Number(valRefMinRaw),
                stroke: '#d32f2f',
                strokeWidth: 1.5,
                strokeDasharray: '4 4',
                ifOverflow: 'extendDomain',
                isFront: true,
              })
            );
          }
          if (Number.isFinite(valRefMaxRaw)) {
            chartChildren.push(
              React.createElement(ReferenceLineAny, {
                y: Number(valRefMaxRaw),
                stroke: '#d32f2f',
                strokeWidth: 1.5,
                strokeDasharray: '4 4',
                ifOverflow: 'extendDomain',
                isFront: true,
              })
            );
          }

          chartChildren.push(
            React.createElement(LineAny, {
              type: 'monotone',
              dataKey: 'valor',
              stroke: '#00A3A8',
              strokeWidth: 2,
              dot: { r: 3 },
              isAnimationActive: false,
            }),
            React.createElement(CartesianGridAny, { stroke: '#ccc', strokeDasharray: '5 5' }),
            React.createElement(XAxisAny, { dataKey: 'fecha' }),
            React.createElement(YAxisAny, yAxisProps),
            React.createElement(TooltipAny, null)
          );

          root.render(
            React.createElement(
              LineChartAny,
              {
                width: chartWidthPx,
                height: chartHeightPx,
                data,
                // Nota: en el PDF damos más margen/width al eje Y para que no se recorten los números
                margin: { top: 5, right: 10, bottom: 5, left: 25 },
              },
              ...chartChildren
            )
          );

          // Dar tiempo a que el SVG quede pintado antes de capturar (evita capturas sin línea)
          await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
          await new Promise<void>((resolve) => setTimeout(() => resolve(), 350));
        }

        try {
          // Capturar como imagen
          const chartCanvas = await html2canvas(chartContainer, { 
            scale: 1.0, 
            logging: false, 
            useCORS: true,
            backgroundColor: 'white'
          });
          
          const chartImgData = chartCanvas.toDataURL('image/png');
          const chartImgWidth = a4Width - (marginSide * 2); // Usar ancho real A4 para que no se salga
          const chartImgHeight = (chartCanvas.height * chartImgWidth) / chartCanvas.width;
          
          // Verificar si cabe en la página actual
          if (chartYPosition + chartImgHeight > pageHeight - 20) {
            pdf.addPage();
            chartYPosition = 20;
          }
          
          pdf.addImage(chartImgData, 'PNG', marginSide, chartYPosition, chartImgWidth, chartImgHeight);
          chartYPosition += chartImgHeight + 15;
          
        } catch (error) {
          console.error('Error al capturar gráfico:', error);
        } finally {
          try {
            root?.unmount();
          } catch {
            // Ignorar
          }
          document.body.removeChild(chartContainer);
        }
      }
    }

    // Agregar numeración a todas las páginas (incluyendo las de gráficos)
    const finalTotalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= finalTotalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100);
      const pageText = `Pág. ${i} de ${finalTotalPages}`;
      const textWidth = pdf.getTextWidth(pageText);
      pdf.text(pageText, 210 - marginSide - textWidth, pageHeight - 8);
    }

    // Descargar el PDF
    onProgress?.(totalSteps, totalSteps, 'Descargando PDF...');
    pdf.save(protocolo.getLetraOrigen() + protocolo.getNumeroPedido() + '.pdf');
  } finally {
    // Eliminar el elemento temporal del DOM
    document.body.removeChild(tempElement);
    onProgress?.(0, 0, '');
  }
};
